import { useEffect, useMemo, useRef, useState } from 'react'
import type { AssessmentQuestion } from './assessment-api'

export type AssessmentQuestionSaveState = 'idle' | 'dirty' | 'saving' | 'saved' | 'error'

type SavePayload = {
  value: unknown
  code: string | null
  display: string | null
  notes: string | null
  version: number
}

type OptionGroup = {
  title: string | null
  tone?: 'standard' | 'separate'
  fullWidth?: boolean
  codes: string[]
}

type QuestionBehaviour = {
  columns?: 2 | 3
  exclusiveCodes?: string[]
  detailsCode?: string
  detailsLabel?: string
}

const questionBehaviours: Record<string, QuestionBehaviour> = {
  'R1-AS-01': { columns: 2, exclusiveCodes: ['minor_easy_correct', 'unknown'], detailsCode: 'other', detailsLabel: 'Describe the other realistic consequence' },
  'R1-AS-06': { columns: 2, exclusiveCodes: ['none', 'unknown'], detailsCode: 'other', detailsLabel: 'Describe the other information involved' },
  'R1-AS-08': { columns: 3, exclusiveCodes: ['none', 'unknown'], detailsCode: 'other', detailsLabel: 'Describe the other action' },
  'R1-AS-13': { columns: 3, exclusiveCodes: ['none', 'unknown'], detailsCode: 'other_regulated', detailsLabel: 'Describe the other regulated or professional area' },
}

type AssessmentQuestionCardProps = {
  question: AssessmentQuestion
  disabled: boolean
  onSave: (value: unknown, code: string | null, display: string | null, notes: string | null) => Promise<unknown>
  onSaveStateChange: (state: AssessmentQuestionSaveState) => void
}

function optionGroups(question: AssessmentQuestion): OptionGroup[] {
  if (question.questionCode === 'R1-AS-01') {
    return [
      { title: 'Information & work', codes: ['incorrect_information', 'rework', 'information_exposure'] },
      { title: 'People & decisions', codes: ['unfair_opportunity', 'service_access'] },
      { title: 'Organisation', codes: ['financial', 'business_disruption'] },
      { title: 'Higher-consequence areas', codes: ['health_safety', 'legal_professional'] },
      { title: 'Other responses', tone: 'separate', codes: ['minor_easy_correct', 'other', 'unknown'] },
    ]
  }

  if (question.questionCode === 'R1-AS-13') {
    return [
      {
        title: 'Regulated or professional areas',
        fullWidth: true,
        codes: ['employment', 'health_care', 'safety', 'finance', 'education', 'regulated_service', 'contractual', 'other_regulated'],
      },
      { title: null, tone: 'separate', codes: ['none', 'unknown'] },
    ]
  }

  return [{ title: null, codes: question.options.map((option) => option.code) }]
}

function textAnswer(question: AssessmentQuestion) {
  return typeof question.answer?.answerValue === 'string'
    ? question.answer.answerValue
    : question.answer?.answerDisplay ?? ''
}

function isLongText(answerType: string) {
  return ['long_text', 'textarea', 'multiline_text'].includes(answerType)
}

export function AssessmentQuestionCard({
  question,
  disabled,
  onSave,
  onSaveStateChange,
}: AssessmentQuestionCardProps) {
  const initialMulti = Array.isArray(question.answer?.answerValue)
    ? question.answer.answerValue.filter((item): item is string => typeof item === 'string')
    : []
  const [selectedCode, setSelectedCode] = useState(question.answer?.answerCode ?? '')
  const [multiValue, setMultiValue] = useState<string[]>(initialMulti)
  const [textValue, setTextValue] = useState(textAnswer(question))
  const [detailsValue, setDetailsValue] = useState(question.answer?.notes ?? '')
  const [saveState, setSaveState] = useState<AssessmentQuestionSaveState>(question.answer ? 'saved' : 'idle')
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const inFlightRef = useRef(false)
  const queuedRef = useRef<SavePayload | null>(null)
  const lastPayloadRef = useRef<SavePayload | null>(null)
  const editVersionRef = useRef(0)
  const groups = useMemo(() => optionGroups(question), [question])
  const behaviour = questionBehaviours[question.questionCode] ?? {}
  const requiresDetails = Boolean(behaviour.detailsCode && multiValue.includes(behaviour.detailsCode))

  useEffect(() => {
    onSaveStateChange(saveState)
  }, [onSaveStateChange, saveState])

  useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current)
  }, [])

  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }

  const persist = async (payload: SavePayload) => {
    clearTimer()
    lastPayloadRef.current = payload

    if (inFlightRef.current) {
      queuedRef.current = payload
      setSaveState('saving')
      return
    }

    inFlightRef.current = true
    setSaveState('saving')

    try {
      await onSave(payload.value, payload.code, payload.display, payload.notes)
      if (payload.version === editVersionRef.current && !queuedRef.current) setSaveState('saved')
    } catch (error) {
      console.error('Formal Assessment answer save failed', error)
      if (payload.version === editVersionRef.current && !queuedRef.current) setSaveState('error')
    } finally {
      inFlightRef.current = false
      const queued = queuedRef.current
      queuedRef.current = null
      if (queued) void persist(queued)
    }
  }

  const schedule = (value: unknown, code: string | null, display: string | null, notes: string | null, delay = 450) => {
    editVersionRef.current += 1
    const payload = { value, code, display, notes, version: editVersionRef.current }
    lastPayloadRef.current = payload
    clearTimer()
    setSaveState('saving')
    timerRef.current = setTimeout(() => void persist(payload), delay)
  }

  const chooseSingle = (code: string, label: string) => {
    setSelectedCode(code)
    editVersionRef.current += 1
    void persist({ value: code, code, display: label, notes: null, version: editVersionRef.current })
  }

  const toggleMulti = (code: string, checked: boolean) => {
    const exclusive = behaviour.exclusiveCodes?.includes(code) ?? false
    const next = checked
      ? (exclusive ? [code] : Array.from(new Set([...multiValue.filter((value) => !behaviour.exclusiveCodes?.includes(value)), code])))
      : multiValue.filter((value) => value !== code)

    setMultiValue(next)
    const nextDetails = behaviour.detailsCode && !next.includes(behaviour.detailsCode) ? '' : detailsValue
    if (nextDetails !== detailsValue) setDetailsValue(nextDetails)
    if (next.length === 0 || (behaviour.detailsCode && next.includes(behaviour.detailsCode) && !nextDetails.trim())) {
      editVersionRef.current += 1
      clearTimer()
      lastPayloadRef.current = null
      setSaveState('dirty')
      return
    }

    const labels = question.options
      .filter((option) => next.includes(option.code))
      .map((option) => option.label)
    schedule(next, next.length === 1 ? next[0] ?? null : null, labels.join(', '), nextDetails.trim() || null)
  }

  const updateDetails = (value: string) => {
    setDetailsValue(value)
    if (!behaviour.detailsCode) return
    if (!value.trim()) {
      editVersionRef.current += 1
      clearTimer()
      lastPayloadRef.current = null
      setSaveState('dirty')
      return
    }
    const labels = question.options
      .filter((option) => multiValue.includes(option.code))
      .map((option) => option.label)
    schedule(multiValue, multiValue.length === 1 ? multiValue[0] ?? null : null, labels.join(', '), value.trim())
  }

  const updateText = (value: string) => {
    setTextValue(value)
    if (!value.trim()) {
      editVersionRef.current += 1
      clearTimer()
      lastPayloadRef.current = null
      setSaveState('dirty')
      return
    }
    schedule(value, null, value, null)
  }

  const saveTextNow = () => {
    if (!textValue.trim()) return
    const pending = lastPayloadRef.current
    if (pending && timerRef.current) void persist(pending)
  }

  const retry = () => {
    const payload = lastPayloadRef.current
    if (payload) void persist(payload)
  }

  const renderOption = (option: AssessmentQuestion['options'][number], type: 'radio' | 'checkbox') => {
    const selected = type === 'radio' ? selectedCode === option.code : multiValue.includes(option.code)
    return (
      <label className={'assessment-option-row' + (selected ? ' selected' : '')} key={option.code}>
        <input
          type={type}
          name={question.questionVersionId}
          value={option.code}
          checked={selected}
          disabled={disabled}
          onChange={(event) => {
            if (type === 'radio') chooseSingle(option.code, option.label)
            else toggleMulti(option.code, event.target.checked)
          }}
        />
        <span className="assessment-option-label">{option.label}</span>
        {selected ? <span className="assessment-option-selected" aria-hidden="true">✓</span> : null}
      </label>
    )
  }

  const isMulti = question.answerType === 'multi_select'
  const isSingle = question.answerType === 'single_select'
    || question.answerType === 'confirmation'
    || (!isMulti && question.options.length > 0)
  const isText = !isMulti && !isSingle

  return (
    <section className="panel assessment-question" aria-labelledby={question.questionCode + '-prompt'}>
      <header className="assessment-question-heading">
        <p className="eyebrow">{question.title}</p>
        <h3 id={question.questionCode + '-prompt'}>{question.prompt}</h3>
        {question.helpText ? <p id={question.questionCode + '-help'} className="assessment-question-help">{question.helpText}</p> : null}
      </header>

      {(isSingle || isMulti) ? (
        <fieldset
          className="assessment-answer-fieldset"
          disabled={disabled}
          aria-describedby={question.helpText ? question.questionCode + '-help' : undefined}
        >
          <legend className="assessment-sr-only">{question.prompt}</legend>
          <div className={'assessment-option-groups' + (isMulti ? ' multi' : '') + (behaviour.columns ? ' columns-' + behaviour.columns : '')}>
            {groups.map((group, index) => {
              const options = group.codes
                .map((code) => question.options.find((option) => option.code === code))
                .filter((option): option is AssessmentQuestion['options'][number] => Boolean(option))
              if (options.length === 0) return null

              return <div className={'assessment-option-group ' + (group.tone ?? 'standard') + (group.fullWidth ? ' full-width' : '')} key={(group.title ?? 'options') + index}>
                {group.title ? <h4>{group.title}</h4> : null}
                <div className="assessment-option-list">
                  {options.map((option) => renderOption(option, isMulti ? 'checkbox' : 'radio'))}
                </div>
              </div>
            })}
          </div>
          {isMulti && requiresDetails ? (
            <label className="assessment-option-details">
              <span>{behaviour.detailsLabel}</span>
              <textarea
                rows={3}
                value={detailsValue}
                disabled={disabled}
                onChange={(event) => updateDetails(event.target.value)}
                aria-describedby={question.helpText ? question.questionCode + '-help' : undefined}
              />
            </label>
          ) : null}
        </fieldset>
      ) : null}

      {isText ? (
        <label className="assessment-text-answer">
          <span className="assessment-sr-only">Your answer</span>
          {isLongText(question.answerType) ? (
            <textarea
              rows={5}
              value={textValue}
              disabled={disabled}
              onChange={(event) => updateText(event.target.value)}
              onBlur={saveTextNow}
            />
          ) : (
            <input
              type="text"
              value={textValue}
              disabled={disabled}
              onChange={(event) => updateText(event.target.value)}
              onBlur={saveTextNow}
            />
          )}
        </label>
      ) : null}

      <div className="assessment-save-status" aria-live="polite">
        {saveState === 'saving' ? <span>Saving…</span> : null}
        {saveState === 'saved' ? <span className="saved">✓ Saved</span> : null}
        {saveState === 'dirty' && isMulti ? <span>{requiresDetails ? 'Add a short description before continuing.' : 'Choose at least one answer.'}</span> : null}
        {saveState === 'dirty' && isText ? <span>Enter an answer before continuing.</span> : null}
        {saveState === 'error' ? <div className="assessment-save-error" role="alert">
          <span><strong>Couldn't save this answer.</strong> Please try again before leaving this assessment.</span>
          <button className="button secondary" type="button" onClick={retry}>Try again</button>
        </div> : null}
      </div>
    </section>
  )
}
